import * as Sentry from "@sentry/react";
import mixpanel from "mixpanel-browser";
import { type EventContext, getNetworkProperties, getRoutePattern } from "./analyticsContext";
import { BaseLogger, PROJECT_GROUP_KEY } from "./baseLogger";
import { redactSearchUrl } from "./redactSearchUrl";

export const CLIENT_EVENT = {
  // ── 로그인 ───────────────────────────────────────────────
  LOGIN_VIEW: "login_view",
  LOGIN_CLICK: "login_click",
  LOGIN_SUCCESS: "login_success",
  LOGIN_ABANDON: "login_abandon",

  // ── 푸시 알림 ─────────────────────────────────────────────
  PUSH_PROMPT_VIEW: "push_prompt_view",
  PUSH_DENIED_VIEW: "push_denied_view",
  PUSH_ENABLE_CLICK: "push_enable_click",
  PUSH_SUBSCRIBE_SUCCESS: "push_subscribe_success",
  PUSH_SUBSCRIBE_FAIL: "push_subscribe_fail",
  PUSH_NOTIFICATION_CLICK: "push_notification_click",

  // ── 조회(소비) ────────────────────────────────────────────
  HOME_VIEW: "home_view",
  HOME_PROJECT_CLICK: "home_project_click",
  HOME_RECENT_LOG_CLICK: "home_recent_log_click",
  AI_SUMMARY_VIEW: "ai_summary_view",
  AI_SUMMARY_EXPAND: "ai_summary_expand",
  PROJECT_DETAIL_VIEW: "project_detail_view",
  PROJECT_TAB_VIEW: "project_tab_view",
  DAILY_LOG_DETAIL_VIEW: "daily_log_detail_view",
  COMMENTS_VIEW: "comments_view",
  FILE_HISTORY_VIEW: "file_history_view",
  WORK_HISTORY_VIEW: "work_history_view",
  WORK_HISTORY_DAY_VIEW: "work_history_day_view",
  IMAGE_LIGHTBOX_OPEN: "image_lightbox_open",
  IMAGE_LIGHTBOX_CLOSE: "image_lightbox_close",

  // ── 공사일지 검색 ─────────────────────────────────────────
  DAILY_LOG_SEARCH_APPLIED: "daily_log_search_applied",
  DAILY_LOG_SEARCH_COMPLETED: "daily_log_search_completed",
  DAILY_LOG_SEARCH_RESULT_OPENED: "daily_log_search_result_opened",
  DAILY_LOG_SEARCH_RESTORED: "daily_log_search_restored",

  // ── 일일 로그 작성 퍼널 ────────────────────────────────────
  DAILY_LOG_WRITE_START: "daily_log_write_start",
  DAILY_LOG_STEP_VIEW: "daily_log_step_view",
  DAILY_LOG_SUBMIT_START: "daily_log_submit_start",
  DAILY_LOG_SUBMIT_SUCCESS: "daily_log_submit_success",
  DAILY_LOG_SUBMIT_FAIL: "daily_log_submit_fail",
  DAILY_LOG_WRITE_ABANDON: "daily_log_write_abandon",

  // ── 편집 락 경합 ──────────────────────────────────────────
  DAILY_LOG_EDIT_CLICK: "daily_log_edit_click",
  DAILY_LOG_LOCK_CONFLICT: "daily_log_lock_conflict",
  DAILY_LOG_LOCK_STATE: "daily_log_lock_state",

  // ── 이미지 업로드 ─────────────────────────────────────────
  IMAGE_UPLOAD_START: "image_upload_start",
  IMAGE_UPLOAD_SUCCESS: "image_upload_success",
  IMAGE_UPLOAD_FAIL: "image_upload_fail",
  IMAGE_UPLOAD_RETRY: "image_upload_retry",
  IMAGE_UPLOAD_REJECTED: "image_upload_rejected",

  // ── 드래프트 ─────────────────────────────────────────────
  DRAFT_CREATED: "draft_created",
  DRAFT_RESTORED: "draft_restored",
  DRAFT_VERSION_CONFLICT: "draft_version_conflict",
  DRAFT_DELETED: "draft_deleted",

  // ── 오프라인 / 네트워크 ────────────────────────────────────
  OFFLINE_PERIOD: "offline_period",
  API_ERROR: "api_error",

  // ── PWA 설치 / 앱 업데이트 ─────────────────────────────────
  PWA_INSTALL_PROMPT_VIEW: "pwa_install_prompt_view",
  PWA_INSTALL_CLICK: "pwa_install_click",
  PWA_INSTALL_RESULT: "pwa_install_result",
  PWA_INSTALL_PROMPT_DISMISS: "pwa_install_prompt_dismiss",
  PWA_INSTALLED: "pwa_installed",
  APP_UPDATE_BANNER_VIEW: "app_update_banner_view",
  APP_UPDATE_CLICK: "app_update_click",
  APP_UPDATE_DISMISS: "app_update_dismiss",

  // ── 기능 사용률 ───────────────────────────────────────────
  COMMENT_SUBMIT_SUCCESS: "comment_submit_success",
  COMMENT_SUBMIT_FAIL: "comment_submit_fail",
  COMMENT_DELETE: "comment_delete",
  WORK_HISTORY_EXPORT_CLICK: "work_history_export_click",
  WORK_HISTORY_EXPORT_SUCCESS: "work_history_export_success",
  WORK_HISTORY_EXPORT_FAIL: "work_history_export_fail",
  IMAGE_ZIP_DOWNLOAD_START: "image_zip_download_start",
  IMAGE_ZIP_DOWNLOAD_SUCCESS: "image_zip_download_success",
  IMAGE_ZIP_DOWNLOAD_FAIL: "image_zip_download_fail",
  IMAGE_ZIP_DOWNLOAD_CANCEL: "image_zip_download_cancel",
} as const;

type ClientEventName = (typeof CLIENT_EVENT)[keyof typeof CLIENT_EVENT];

/* ------------------------------------------------------------------ */
/* 이벤트 스키마                                                        */
/* ------------------------------------------------------------------ */

/**
 * 엔티티 식별자는 **항상 문자열**로 맞춘다.
 *
 * 라우트 파라미터는 문자열, API 응답은 숫자로 같은 id 를 내려주기 때문에 그대로 보내면
 * 한 프로퍼티에 두 타입이 섞인다. Mixpanel 은 이런 프로퍼티의 필터·조인을 조용히
 * 어긋나게 만들므로, 타입으로 강제해 호출부에서 `String()` 을 거치게 한다.
 */
type ProjectScoped = { project_id: string };
type LogScoped = ProjectScoped & { log_id: string };

/** 프로퍼티가 없는 이벤트. */
type NoProperties = Record<string, never>;

type WriteMode = "create" | "edit";

/**
 * 이벤트별로 허용되는 프로퍼티.
 *
 * 이 map 이 계측의 단일 계약서다. 여기 없는 키를 보내거나 필수 키를 빠뜨리면
 * 컴파일이 깨지므로, 오타·누락·타입 혼재가 배포 전에 잡힌다.
 */
type EventPropertyMap = {
  [CLIENT_EVENT.LOGIN_VIEW]: { is_retry: boolean };
  [CLIENT_EVENT.LOGIN_CLICK]: { provider: "google" };
  /** `duration_ms` 가 `null` 이면 로그인이 일어난 건 확실하지만 소요 시간은 믿을 수 없다는 뜻. */
  [CLIENT_EVENT.LOGIN_SUCCESS]: { provider: "google"; duration_ms: number | null };
  [CLIENT_EVENT.LOGIN_ABANDON]: { provider: "google"; duration_ms: number | null };

  [CLIENT_EVENT.PUSH_PROMPT_VIEW]: NoProperties;
  [CLIENT_EVENT.PUSH_DENIED_VIEW]: NoProperties;
  [CLIENT_EVENT.PUSH_ENABLE_CLICK]: NoProperties;
  [CLIENT_EVENT.PUSH_SUBSCRIBE_SUCCESS]: NoProperties;
  [CLIENT_EVENT.PUSH_SUBSCRIBE_FAIL]: NoProperties;
  [CLIENT_EVENT.PUSH_NOTIFICATION_CLICK]: { redirect_url: string };

  [CLIENT_EVENT.HOME_VIEW]: NoProperties;
  [CLIENT_EVENT.HOME_PROJECT_CLICK]: ProjectScoped & { position: number };
  [CLIENT_EVENT.HOME_RECENT_LOG_CLICK]: LogScoped & { position: number };
  [CLIENT_EVENT.AI_SUMMARY_VIEW]: { summary_length: number };
  [CLIENT_EVENT.AI_SUMMARY_EXPAND]: { is_expanded: boolean; summary_length: number };
  [CLIENT_EVENT.PROJECT_DETAIL_VIEW]: ProjectScoped;
  [CLIENT_EVENT.PROJECT_TAB_VIEW]: ProjectScoped & { tab: "overview" | "documents" };
  [CLIENT_EVENT.DAILY_LOG_DETAIL_VIEW]: LogScoped & {
    image_count: number;
    tag_count: number;
    workforce_count: number;
    equipment_count: number;
    description_length: number;
    is_owner: boolean;
    /** 작성일로부터 며칠 지난 로그를 보고 있는지. 0 이면 당일 기록. */
    days_since_log_date: number;
  };
  [CLIENT_EVENT.COMMENTS_VIEW]: LogScoped;
  [CLIENT_EVENT.FILE_HISTORY_VIEW]: LogScoped;
  [CLIENT_EVENT.WORK_HISTORY_VIEW]: ProjectScoped;
  [CLIENT_EVENT.WORK_HISTORY_DAY_VIEW]: ProjectScoped & { date: string };
  [CLIENT_EVENT.IMAGE_LIGHTBOX_OPEN]: LogScoped & { image_count: number; index: number };
  [CLIENT_EVENT.IMAGE_LIGHTBOX_CLOSE]: LogScoped & {
    image_count: number;
    /** 라이트박스를 여는 동안 실제로 도달한 서로 다른 사진 수. */
    viewed_count: number;
    duration_ms: number;
  };

  /**
   * 공사일지 검색. 검색어 원문·발췌·업체 이름·cursor는 싣지 않는다 (TRD §9.2).
   * `request_id` 는 새 조건 적용·명시적 갱신·상세 복귀 갱신마다 FE가 만드는 UUID다.
   * 같은 조회의 자동 재시도·추가 페이지는 같은 ID를 쓴다.
   */
  [CLIENT_EVENT.DAILY_LOG_SEARCH_APPLIED]: ProjectScoped & {
    has_keyword: boolean;
    keyword_length_bucket: "0" | "1-2" | "3-10" | "11-30" | "31+";
    filter_count: number;
    trigger: "typing" | "enter" | "clear" | "filter" | "remove" | "reset" | "fix_invalid";
  };
  [CLIENT_EVENT.DAILY_LOG_SEARCH_COMPLETED]: ProjectScoped & {
    request_id: string;
    duration_ms: number;
    result_count: number;
    device: "mobile" | "desktop";
    source: "query" | "refresh" | "restore";
  };
  [CLIENT_EVENT.DAILY_LOG_SEARCH_RESULT_OPENED]: LogScoped & {
    row_position: number;
    /** 표시 중인 결과를 만든 조회. 캐시에서 바로 보여 준 결과라 조회가 없었으면 `null`. */
    request_id: string | null;
    open_method: "same_tab" | "new_tab";
  };
  [CLIENT_EVENT.DAILY_LOG_SEARCH_RESTORED]: ProjectScoped & {
    cache_hit: boolean;
    restored: boolean;
    fallback_reason: "nearby" | "date" | "scroll_top" | "no_rows" | "fetch_failed" | null;
    loaded_page_count: number;
  };

  [CLIENT_EVENT.DAILY_LOG_WRITE_START]: ProjectScoped & { mode: WriteMode; draft_id: string | null };
  [CLIENT_EVENT.DAILY_LOG_STEP_VIEW]: ProjectScoped & {
    mode: WriteMode;
    step: string;
    step_index: number;
    /** 직전 단계에서 머문 시간. 첫 단계면 `null`. */
    time_since_previous_step_ms: number | null;
    /** 이 단계에 몇 번째로 들어왔는지. 2 이상이면 되돌아온 것이다. */
    step_visit_count: number;
  };
  [CLIENT_EVENT.DAILY_LOG_SUBMIT_START]: ProjectScoped & { mode: WriteMode; duration_ms: number };
  [CLIENT_EVENT.DAILY_LOG_SUBMIT_SUCCESS]: ProjectScoped & {
    mode: WriteMode;
    draft_id: string | null;
    duration_ms: number;
    image_count: number;
    workforce_entry_count: number;
    workforce_count: number;
    equipment_entry_count: number;
    equipment_count: number;
    tag_count: number;
    description_length: number;
  };
  [CLIENT_EVENT.DAILY_LOG_SUBMIT_FAIL]: ProjectScoped & {
    mode: WriteMode;
    draft_id: string | null;
    duration_ms: number;
    reason: "validation" | "api";
    error_code: string | null;
    last_step: string;
    /** validation 으로 막힌 필드 이름. 어느 입력이 사용자를 붙잡는지 특정하는 축이다. */
    failed_fields: string[];
    failed_field_count: number;
  };
  [CLIENT_EVENT.DAILY_LOG_WRITE_ABANDON]: ProjectScoped & {
    mode: WriteMode;
    draft_id: string | null;
    reason: "user_exit" | "redirected" | "session_end";
    last_step: string;
    last_step_index: number;
    duration_ms: number;
    /** 떠나기 직전 단계에서 머문 시간. 어느 단계가 사람을 붙잡는지 가리킨다. */
    last_step_duration_ms: number;
  };

  [CLIENT_EVENT.DAILY_LOG_EDIT_CLICK]: LogScoped & {
    /** 이미 내가 쥔 락이 있어 서버 요청 없이 편집으로 넘어간 경우 `true`. */
    has_own_lock: boolean;
  };
  [CLIENT_EVENT.DAILY_LOG_LOCK_CONFLICT]: LogScoped & {
    /** `edit_click`: 편집 진입 시도가 막힘. `edit_route`: 편집 화면에서 밀려남. */
    source: "edit_click" | "edit_route";
    error_code: string | null;
  };
  /**
   * 로그를 열었을 때 **확정된** 편집 락 상태. 조회 1건당 최대 1건.
   *
   * 조회 이벤트에 싣지 않고 분리한다. 락 조회는 비동기라 조회 이벤트에 실으면
   * "확정될 때까지 기다려 조회수를 잃거나, 기다리지 않고 미확정 값을 확정값으로 굳히거나"
   * 둘 중 하나가 된다. 확인하지 못한 경우는 이벤트 자체가 없다.
   */
  [CLIENT_EVENT.DAILY_LOG_LOCK_STATE]: LogScoped & { is_locked_by_other: boolean };

  [CLIENT_EVENT.IMAGE_UPLOAD_START]: ProjectScoped & {
    /** 파일 선택 1회를 묶는 키. 선택 → 성공/실패 전환율을 세려면 이게 필요하다. */
    upload_session_id: string;
    draft_id: string | null;
    selected_count: number;
    queued_count: number;
    heic_count: number;
    heic_missing_mime_count: number;
    empty_mime_count: number;
    total_bytes: number;
    already_committed_count: number;
  };
  [CLIENT_EVENT.IMAGE_UPLOAD_SUCCESS]: ProjectScoped & {
    upload_session_id: string | null;
    draft_id: string | null;
    file_size: number;
    mime_type: string;
    is_heic: boolean;
    is_heic_missing_mime: boolean;
    duration_ms: number | null;
    attempt: number;
  };
  [CLIENT_EVENT.IMAGE_UPLOAD_FAIL]: ProjectScoped & {
    upload_session_id: string | null;
    draft_id: string | null;
    file_size: number;
    mime_type: string;
    is_heic: boolean;
    is_heic_missing_mime: boolean;
    duration_ms: number | null;
    attempt: number;
    error_message: string;
  };
  [CLIENT_EVENT.IMAGE_UPLOAD_RETRY]: ProjectScoped & {
    upload_session_id: string | null;
    draft_id: string | null;
    attempt: number;
    file_size: number | null;
    is_heic: boolean | null;
  };
  [CLIENT_EVENT.IMAGE_UPLOAD_REJECTED]: ProjectScoped & {
    /** 거부도 같은 선택에서 나온 결과다. 없으면 "고른 10장 중 2장 거부"를 세션 단위로 잇지 못한다. */
    upload_session_id: string;
    draft_id: string | null;
    selected_count: number;
    rejected_count: number;
    rejected_unsupported_type: number;
    rejected_over_file_size: number;
    rejected_over_max_images: number;
    max_images: number;
    max_file_size_bytes: number;
    rejected_sizes: number[];
    rejected_unsupported_mime_types?: string[];
    rejected_unsupported_extensions?: string[];
    rejected_unsupported_heic_count?: number;
  };

  [CLIENT_EVENT.DRAFT_CREATED]: ProjectScoped & { draft_id: string; source: "new" | "resumed" };
  [CLIENT_EVENT.DRAFT_RESTORED]: ProjectScoped & {
    draft_id: string;
    source: string;
    resolved_version: number;
    local_version: number | null;
    server_version: number | null;
    is_server_newer: boolean;
  };
  [CLIENT_EVENT.DRAFT_VERSION_CONFLICT]: ProjectScoped & {
    draft_id: string;
    local_version: number;
    server_version: number | null;
  };
  [CLIENT_EVENT.DRAFT_DELETED]: ProjectScoped & { draft_id: string };

  [CLIENT_EVENT.OFFLINE_PERIOD]: {
    duration_ms: number;
    page: string;
    ended_by: "reconnect" | "session_end";
    is_duration_exact: boolean;
  };
  [CLIENT_EVENT.API_ERROR]: {
    endpoint_pattern: string;
    method: string | null;
    status: number | null;
    error_code: string | null;
    duration_ms: number | null;
    is_network_error: boolean;
    is_timeout: boolean;
    is_expected: boolean;
  };

  [CLIENT_EVENT.PWA_INSTALL_PROMPT_VIEW]: { platform: "android" | "ios" };
  [CLIENT_EVENT.PWA_INSTALL_CLICK]: { platform: "android" };
  [CLIENT_EVENT.PWA_INSTALL_RESULT]: { platform: "android"; outcome: "accepted" | "dismissed" };
  [CLIENT_EVENT.PWA_INSTALL_PROMPT_DISMISS]: { platform: "android" | "ios" };
  [CLIENT_EVENT.PWA_INSTALLED]: NoProperties;
  [CLIENT_EVENT.APP_UPDATE_BANNER_VIEW]: NoProperties;
  [CLIENT_EVENT.APP_UPDATE_CLICK]: NoProperties;
  [CLIENT_EVENT.APP_UPDATE_DISMISS]: NoProperties;

  [CLIENT_EVENT.COMMENT_SUBMIT_SUCCESS]: LogScoped & { content_length: number };
  [CLIENT_EVENT.COMMENT_SUBMIT_FAIL]: LogScoped & { content_length: number };
  [CLIENT_EVENT.COMMENT_DELETE]: LogScoped;
  [CLIENT_EVENT.WORK_HISTORY_EXPORT_CLICK]: ProjectScoped & { range_days: number };
  [CLIENT_EVENT.WORK_HISTORY_EXPORT_SUCCESS]: ProjectScoped & { range_days: number; duration_ms: number };
  [CLIENT_EVENT.WORK_HISTORY_EXPORT_FAIL]: ProjectScoped & { range_days: number; duration_ms: number };
  [CLIENT_EVENT.IMAGE_ZIP_DOWNLOAD_START]: LogScoped & { image_count: number };
  [CLIENT_EVENT.IMAGE_ZIP_DOWNLOAD_SUCCESS]: LogScoped & { image_count: number; source: "job" | "cached" };
  [CLIENT_EVENT.IMAGE_ZIP_DOWNLOAD_FAIL]: LogScoped & {
    image_count: number;
    reason: "polling_timeout" | "job_failed" | "file_not_ready" | "fetch_failed" | "retry_failed" | "job_create_failed";
    error_code?: string | null;
  };
  [CLIENT_EVENT.IMAGE_ZIP_DOWNLOAD_CANCEL]: LogScoped & { image_count: number };
};

/** 프로퍼티가 없는 이벤트는 인자를 아예 받지 않는다. */
type PropertyArgs<E extends ClientEventName> = EventPropertyMap[E] extends NoProperties
  ? []
  : [properties: EventPropertyMap[E]];

type TrackArgs<E extends ClientEventName> = EventPropertyMap[E] extends NoProperties
  ? [properties?: undefined, options?: SendOptions]
  : [properties: EventPropertyMap[E], options?: SendOptions];

/* ------------------------------------------------------------------ */
/* 전송                                                                */
/* ------------------------------------------------------------------ */

const EVENT_ACTION = {
  CLICK: "click",
  VIEW: "view",
  /** 사용자의 직접 입력이 아니라 그 결과(성공/실패)를 기록하는 이벤트 */
  RESULT: "result",
} as const;

type EventAction = (typeof EVENT_ACTION)[keyof typeof EVENT_ACTION];

/**
 * 이벤트 이름별 `action`.
 *
 * `action` 을 호출부에서 고르게 두면 같은 이벤트가 화면마다 다른 값으로 나가고,
 * `action` 으로 breakdown 한 순간 한 이벤트가 두 줄로 갈라진다. 실제로
 * `daily_log_lock_conflict` 가 상세 화면에서는 `result`, 편집 라우트에서는
 * action 없이 나가고 있었다. 그래서 이름당 한 번만 정하고, 아래 `EventLogger` 의
 * 메서드가 이 표와 어긋나면 **컴파일이 깨지도록** 묶어 둔다.
 *
 * `null` 은 사용자의 직접 동작에 대응하지 않는 시스템·생명주기 이벤트다.
 */
const EVENT_ACTION_BY_NAME = {
  [CLIENT_EVENT.LOGIN_VIEW]: EVENT_ACTION.VIEW,
  [CLIENT_EVENT.LOGIN_CLICK]: EVENT_ACTION.CLICK,
  [CLIENT_EVENT.LOGIN_SUCCESS]: EVENT_ACTION.RESULT,
  [CLIENT_EVENT.LOGIN_ABANDON]: null,

  [CLIENT_EVENT.PUSH_PROMPT_VIEW]: EVENT_ACTION.VIEW,
  [CLIENT_EVENT.PUSH_DENIED_VIEW]: EVENT_ACTION.VIEW,
  [CLIENT_EVENT.PUSH_ENABLE_CLICK]: EVENT_ACTION.CLICK,
  [CLIENT_EVENT.PUSH_SUBSCRIBE_SUCCESS]: EVENT_ACTION.RESULT,
  [CLIENT_EVENT.PUSH_SUBSCRIBE_FAIL]: EVENT_ACTION.RESULT,
  [CLIENT_EVENT.PUSH_NOTIFICATION_CLICK]: EVENT_ACTION.CLICK,

  [CLIENT_EVENT.HOME_VIEW]: EVENT_ACTION.VIEW,
  [CLIENT_EVENT.HOME_PROJECT_CLICK]: EVENT_ACTION.CLICK,
  [CLIENT_EVENT.HOME_RECENT_LOG_CLICK]: EVENT_ACTION.CLICK,
  [CLIENT_EVENT.AI_SUMMARY_VIEW]: EVENT_ACTION.VIEW,
  [CLIENT_EVENT.AI_SUMMARY_EXPAND]: EVENT_ACTION.CLICK,
  [CLIENT_EVENT.PROJECT_DETAIL_VIEW]: EVENT_ACTION.VIEW,
  [CLIENT_EVENT.PROJECT_TAB_VIEW]: EVENT_ACTION.VIEW,
  [CLIENT_EVENT.DAILY_LOG_DETAIL_VIEW]: EVENT_ACTION.VIEW,
  [CLIENT_EVENT.COMMENTS_VIEW]: EVENT_ACTION.VIEW,
  [CLIENT_EVENT.FILE_HISTORY_VIEW]: EVENT_ACTION.VIEW,
  [CLIENT_EVENT.WORK_HISTORY_VIEW]: EVENT_ACTION.VIEW,
  [CLIENT_EVENT.WORK_HISTORY_DAY_VIEW]: EVENT_ACTION.VIEW,
  [CLIENT_EVENT.IMAGE_LIGHTBOX_OPEN]: EVENT_ACTION.VIEW,
  // 닫힘은 사용자의 동작이 아니라 열람 구간의 요약이다.
  [CLIENT_EVENT.IMAGE_LIGHTBOX_CLOSE]: null,

  // 입력이 멈춰 적용되는 경우도 있어 클릭으로 세지 않는다.
  [CLIENT_EVENT.DAILY_LOG_SEARCH_APPLIED]: null,
  [CLIENT_EVENT.DAILY_LOG_SEARCH_COMPLETED]: EVENT_ACTION.RESULT,
  [CLIENT_EVENT.DAILY_LOG_SEARCH_RESULT_OPENED]: EVENT_ACTION.CLICK,
  [CLIENT_EVENT.DAILY_LOG_SEARCH_RESTORED]: null,

  [CLIENT_EVENT.DAILY_LOG_WRITE_START]: null,
  [CLIENT_EVENT.DAILY_LOG_STEP_VIEW]: EVENT_ACTION.VIEW,
  [CLIENT_EVENT.DAILY_LOG_SUBMIT_START]: EVENT_ACTION.CLICK,
  [CLIENT_EVENT.DAILY_LOG_SUBMIT_SUCCESS]: EVENT_ACTION.RESULT,
  [CLIENT_EVENT.DAILY_LOG_SUBMIT_FAIL]: EVENT_ACTION.RESULT,
  [CLIENT_EVENT.DAILY_LOG_WRITE_ABANDON]: null,

  [CLIENT_EVENT.DAILY_LOG_EDIT_CLICK]: EVENT_ACTION.CLICK,
  // 편집 진입 시도의 결과다. 상세 화면에서 나든 편집 라우트에서 나든 같은 값이어야 한다.
  [CLIENT_EVENT.DAILY_LOG_LOCK_CONFLICT]: EVENT_ACTION.RESULT,
  // 사용자의 동작이 아니라 화면을 열었을 때 관측된 상태다.
  [CLIENT_EVENT.DAILY_LOG_LOCK_STATE]: null,

  [CLIENT_EVENT.IMAGE_UPLOAD_START]: null,
  [CLIENT_EVENT.IMAGE_UPLOAD_SUCCESS]: EVENT_ACTION.RESULT,
  [CLIENT_EVENT.IMAGE_UPLOAD_FAIL]: EVENT_ACTION.RESULT,
  [CLIENT_EVENT.IMAGE_UPLOAD_RETRY]: EVENT_ACTION.CLICK,
  [CLIENT_EVENT.IMAGE_UPLOAD_REJECTED]: null,

  [CLIENT_EVENT.DRAFT_CREATED]: null,
  [CLIENT_EVENT.DRAFT_RESTORED]: null,
  [CLIENT_EVENT.DRAFT_VERSION_CONFLICT]: null,
  [CLIENT_EVENT.DRAFT_DELETED]: EVENT_ACTION.RESULT,

  [CLIENT_EVENT.OFFLINE_PERIOD]: null,
  [CLIENT_EVENT.API_ERROR]: null,

  [CLIENT_EVENT.PWA_INSTALL_PROMPT_VIEW]: EVENT_ACTION.VIEW,
  [CLIENT_EVENT.PWA_INSTALL_CLICK]: EVENT_ACTION.CLICK,
  [CLIENT_EVENT.PWA_INSTALL_RESULT]: EVENT_ACTION.RESULT,
  [CLIENT_EVENT.PWA_INSTALL_PROMPT_DISMISS]: EVENT_ACTION.CLICK,
  [CLIENT_EVENT.PWA_INSTALLED]: null,
  [CLIENT_EVENT.APP_UPDATE_BANNER_VIEW]: EVENT_ACTION.VIEW,
  [CLIENT_EVENT.APP_UPDATE_CLICK]: EVENT_ACTION.CLICK,
  [CLIENT_EVENT.APP_UPDATE_DISMISS]: EVENT_ACTION.CLICK,

  [CLIENT_EVENT.COMMENT_SUBMIT_SUCCESS]: EVENT_ACTION.RESULT,
  [CLIENT_EVENT.COMMENT_SUBMIT_FAIL]: EVENT_ACTION.RESULT,
  [CLIENT_EVENT.COMMENT_DELETE]: EVENT_ACTION.RESULT,
  [CLIENT_EVENT.WORK_HISTORY_EXPORT_CLICK]: EVENT_ACTION.CLICK,
  [CLIENT_EVENT.WORK_HISTORY_EXPORT_SUCCESS]: EVENT_ACTION.RESULT,
  [CLIENT_EVENT.WORK_HISTORY_EXPORT_FAIL]: EVENT_ACTION.RESULT,
  [CLIENT_EVENT.IMAGE_ZIP_DOWNLOAD_START]: EVENT_ACTION.CLICK,
  [CLIENT_EVENT.IMAGE_ZIP_DOWNLOAD_SUCCESS]: EVENT_ACTION.RESULT,
  [CLIENT_EVENT.IMAGE_ZIP_DOWNLOAD_FAIL]: EVENT_ACTION.RESULT,
  [CLIENT_EVENT.IMAGE_ZIP_DOWNLOAD_CANCEL]: EVENT_ACTION.CLICK,
} as const satisfies Record<ClientEventName, EventAction | null>;

/** 선언된 `action` 이 `A` 인 이벤트 이름만 추린다. */
type EventNamesWithAction<A extends EventAction | null> = {
  [K in ClientEventName]: (typeof EVENT_ACTION_BY_NAME)[K] extends A ? K : never;
}[ClientEventName];

/**
 * 모든 클라이언트 이벤트에 공통으로 붙는 프로퍼티.
 *
 * `is_offline` 은 이벤트 발생 시점의 연결 상태다. mixpanel-browser 는 전송 실패한
 * 배치를 localStorage 에 보관했다가 재전송하므로, 오프라인에서 발생한 이벤트도
 * 온라인 복귀 후 이 플래그를 단 채로 도착한다.
 *
 * `route_pattern` 은 식별자가 치환된 화면 단위 키다. `pathname` 은 값마다 항목이
 * 갈라져 breakdown 에 쓸 수 없으므로 둘을 함께 보낸다(원본 추적은 `pathname`).
 *
 * 네트워크 프로퍼티는 `is_offline` 이 잡지 못하는 "연결은 됐지만 느린" 구간을 가른다.
 * 현장 회선 품질과 업로드·제출 실패의 상관을 보려면 모든 이벤트에 붙어 있어야 한다.
 *
 * 화면과 현장은 기본적으로 **전송 시점**에 읽는다. 관측이 끝난 뒤에야 전송되는 이벤트
 * (이탈, API 응답)는 이미 다음 화면·현장으로 넘어가 있으므로 `context` 로 시작 시점의
 * 값을 넘겨 실제 발생 지점에 귀속시킨다.
 *
 * 현장(group)은 슈퍼 프로퍼티라 넘기지 않으면 전송 시점 값이 붙는다. 스냅샷이 주어지면
 * 관측 시점에 현장이 없었다는 사실까지 포함해 **항상** 덮어쓴다 — 비워두면 그 사이
 * 진입한 다른 현장이 그대로 남기 때문이다.
 */
function getCommonProperties(context?: EventContext) {
  return {
    pathname: context?.pathname ?? location.pathname,
    route_pattern: context ? context.route_pattern : getRoutePattern(),
    ...(context ? { [PROJECT_GROUP_KEY]: context.project_group } : {}),
    is_offline: !navigator.onLine,
    ...getNetworkProperties(),
    // SDK가 모든 이벤트에 붙이는 전체 URL을 덮어쓴다. 검색 목록 URL의 검색어가 분석 도구로 가지 않게 한다.
    $current_url: redactSearchUrl(location.href),
  };
}

export type SendOptions = {
  /**
   * `sendBeacon` 은 페이지가 사라지는 순간(pagehide)에도 전송이 보장되는 대신 응답을 읽을 수 없다.
   * 이탈 시점에만 쓰고, 평상시 이벤트는 기본 transport 를 그대로 둔다.
   */
  transport?: "sendBeacon";
  /**
   * 관측이 **시작된** 시점의 화면·현장. 전송 시점에 이미 다른 화면으로 넘어가 있는
   * 이벤트에만 넘긴다. 넘기지 않으면 전송 시점의 값이 쓰인다.
   */
  context?: EventContext;
};

function send(
  eventName: ClientEventName,
  properties: Record<string, unknown> | undefined,
  options?: SendOptions,
): void {
  try {
    const action = EVENT_ACTION_BY_NAME[eventName];

    mixpanel.track(
      eventName,
      {
        ...properties,
        ...getCommonProperties(options?.context),
        ...(action ? { action } : {}),
      },
      options?.transport ? { transport: options.transport } : undefined,
    );
  } catch {
    Sentry.captureException(
      new Error(`Failed to send event: ${eventName} with properties: ${JSON.stringify(properties)}`),
    );
  }
}

/**
 * 각 메서드는 `EVENT_ACTION_BY_NAME` 에서 그 action 으로 선언된 이벤트만 받는다.
 * 같은 이벤트를 다른 메서드로 보내려 하면 호출부에서 컴파일이 깨진다.
 */
export class EventLogger extends BaseLogger {
  /** 사용자가 직접 누른 동작 */
  public static click<E extends EventNamesWithAction<typeof EVENT_ACTION.CLICK>>(
    eventName: E,
    ...args: PropertyArgs<E>
  ): void {
    send(eventName, args[0]);
  }

  /** 화면/컴포넌트 노출 */
  public static view<E extends EventNamesWithAction<typeof EVENT_ACTION.VIEW>>(
    eventName: E,
    ...args: PropertyArgs<E>
  ): void {
    send(eventName, args[0]);
  }

  /** 클릭의 결과(성공/실패). 클릭 수 집계에 섞이지 않도록 action 을 분리한다. */
  public static result<E extends EventNamesWithAction<typeof EVENT_ACTION.RESULT>>(
    eventName: E,
    ...args: PropertyArgs<E>
  ): void {
    send(eventName, args[0]);
  }

  /** 사용자 입력과 무관한 시스템/생명주기 이벤트 (오프라인 구간, 자동 저장 등) */
  public static track<E extends EventNamesWithAction<null>>(eventName: E, ...args: TrackArgs<E>): void {
    send(eventName, args[0], args[1]);
  }
}
